import type { FilterCondition, FilterRule } from './types';
import { swedishList, FIELD_LABELS_LOWERCASE } from './deriveReason';
import { displayConditionValue } from './labels';

/**
 * Adjective form of ecoStatus values for the clause "gårdar som är {adj}".
 * Distinct from deriveReason's ECO_STATUS_PHRASES (which appends " gårdar").
 */
export const ECO_STATUS_ADJECTIVE: Record<string, string> = {
  Ekologisk: 'ekologiska',
  Konventionell: 'konventionella',
  'Under omställning till ekologisk': 'under omställning',
  'Både konventionell och ekologisk': 'både konventionella och ekologiska (parallellproduktion)',
};

/**
 * Render one condition as an admin-facing clause that reads naturally after
 * "Visas för gårdar som …".
 *
 * Examples:
 * - includesAny ['KRAV','IP'] → "har någon av KRAV eller IP"
 * - excludesAny ['KRAV']      → "inte har KRAV"
 * - isEmpty (certifications)  → "inte har angett certifieringar"
 * - gte 100                   → "har minst 100 djurenheter"
 * - eq ecoStatus ekologisk    → "är ekologiska"
 * - includesAny ecoStatus [Ekologisk, Under omställning…] → "är ekologiska eller under omställning"
 * - excludesAny ecoStatus [Konventionell]                → "inte är konventionella"
 */
function describeClause(condition: FilterCondition): string {
  const { field, operator, value } = condition;
  const label = FIELD_LABELS_LOWERCASE[field] ?? field;
  const list = Array.isArray(value)
    ? swedishList((value as string[]).map((v) => displayConditionValue(field, v)))
    : '';
  // Driftsform is a scalar the engine reads as a one-element set (AG-275);
  // its values are adjectives of the farm, not things the farm "has".
  const ecoList =
    field === 'ecoStatus' && Array.isArray(value)
      ? swedishList((value as string[]).map((v) => ECO_STATUS_ADJECTIVE[v] ?? v))
      : '';

  switch (operator) {
    case 'includesAny':
      if (ecoList) return `är ${ecoList}`;
      return list ? `har någon av ${list}` : `har angett ${label}`;
    case 'includesAll':
      return list ? `har alla av ${list}` : `har angett ${label}`;
    case 'excludesAny':
      if (ecoList) return `inte är ${ecoList}`;
      return list ? `inte har ${list}` : `inte har angett ${label}`;
    case 'isEmpty':
      return `inte har angett ${label}`;
    case 'gte':
      return typeof value === 'number' ? `har minst ${value} djurenheter` : `har angett djurenheter`;
    case 'lte':
      return typeof value === 'number' ? `har högst ${value} djurenheter` : `har angett djurenheter`;
    case 'eq':
      if (field === 'animalUnits' && typeof value === 'number') {
        return `har exakt ${value} djurenheter`;
      }
      if (field === 'ecoStatus' && typeof value === 'string') {
        return `är ${ECO_STATUS_ADJECTIVE[value] ?? value}`;
      }
      return `matchar ${label}`;
    default:
      return `matchar ${label}`;
  }
}

export interface RuleDescription {
  /** True when the rule has at least one usable condition. */
  hasConditions: boolean;
  /** One clause per condition, e.g. ["har någon av KRAV", "har minst 50 djurenheter"]. */
  clauses: string[];
  /** Full "shown" sentence fragment: clauses joined with " och " (AND logic). */
  shownFor: string;
}

/**
 * Produce a plain-Swedish description of who sees a requirement under this rule.
 * Used for the always-on "live echo" in the admin filter editor so the admin
 * never has to reason backwards from boolean conditions to visibility.
 *
 * AND logic across conditions is rendered with " och " between clauses.
 *
 * @param rule The FilterRule being edited, or null.
 */
export function describeRule(rule: FilterRule | null): RuleDescription {
  const conditions = rule?.conditions ?? [];
  if (conditions.length === 0) {
    return { hasConditions: false, clauses: [], shownFor: '' };
  }
  const clauses = conditions.map(describeClause);
  return {
    hasConditions: true,
    clauses,
    shownFor: clauses.join(' och '),
  };
}
